import { buildUrl } from '@/services/path'
import auth0 from 'auth0-js'
import { EventEmitter } from 'events'

/**
 * Name of the event used for login interaction.
 */
export const loginEvent = 'loginEvent'

/**
 * Holds the authentication configuration.
 */
const webAuth = new auth0.WebAuth({
  domain: process.env.VUE_APP_DOMAIN as string,
  redirectUri: process.env.VUE_APP_CALLBACK as string,
  clientID: process.env.VUE_APP_ID as string,
  audience: process.env.VUE_APP_AUDIENCE as string,
  responseType: 'token id_token',
  scope: 'openid profile email',
  overrides: {
    __jwks_uri: `${buildUrl(process.env.VUE_APP_MAIN_UI_API_URL, '/api/v1/auth0/jwks.json')}`
  }
})

/**
 * Basic login state used to require either renew or relogin.
 */
const localStorageKey = 'loggedIn'

/**
 * Authentication service class; does the various login actions and provides the authentication headers.
 */
class AuthenticationService extends EventEmitter {

  /**
   * Local value of the authentication result token.
   */
  public idToken: string | undefined

  /**
   * Token expiration.
   */
  public tokenExpiry: Date | undefined

  /**
   * Current access token.
   */
  public accessToken: any = null

  /**
   * Expiration for access token, in millis.
   */
  public accessTokenExpiry: Date | undefined

  // Starts the user login flow
  public login (customState: any) {
    webAuth.authorize({
      state: customState
    })
  }

  // Handles the callback request from Auth0
  public handleAuthentication () {
    return new Promise((resolve, reject) => {
      webAuth.parseHash((err, authResult) => {
        if (err) {
          reject(err)
        } else {
          this.localLogin(authResult!)
          resolve(authResult!.idToken)
        }
      })
    })
  }

  public localLogin (authResult: auth0.Auth0DecodedHash) {
    this.idToken = authResult.idToken

    // Convert the JWT expiry time from seconds to milliseconds
    this.tokenExpiry = new Date(authResult.idTokenPayload.exp * 1000)

    // Save the Access Token and expiry time in memory
    this.accessToken = authResult.accessToken

    // Convert expiresIn to milliseconds and add the current time
    // (expiresIn is a relative timestamp, but an absolute time is desired)
    this.accessTokenExpiry = new Date(Date.now() + authResult.expiresIn! * 1000)

    localStorage.setItem(localStorageKey, 'true')

    this.emit(loginEvent, {
      loggedIn: true,
      profile: authResult.idTokenPayload,
      state: authResult.state || '{}'
    })
  }

  public renewTokens () {
    return new Promise((resolve, reject) => {
      if (localStorage.getItem(localStorageKey) !== 'true') {
        return reject('Not logged in')
      }

      webAuth.checkSession({}, (err, authResult) => {
        if (err) {
          reject(err)
        } else {
          this.localLogin(authResult)
          resolve(authResult)
        }
      })
    })
  }

  /**
   * Logs the user out of the application.
   */
  public logOut () {
    localStorage.removeItem(localStorageKey)

    this.idToken = undefined
    this.tokenExpiry = undefined

    webAuth.logout({
      returnTo: process.env.BASE_URL
    })

    this.emit(loginEvent, { loggedIn: false })
  }

  /**
   * Checks whether the user is authenticated or not.
   */
  public isAuthenticated () {
    return (
      this.tokenExpiry !== undefined &&
      Date.now() < this.tokenExpiry.getTime() &&
      localStorage.getItem(localStorageKey) === 'true'
    )
  }

  /**
   * Provides authentication headers.
   */
  public headers () {
    return this.getAccessToken().then((accessToken) => {
      return { Authorization: 'Bearer ' + accessToken }
    })
  }

  /**
   * Checks whether the current access token is valid or not.
   */
  private isAccessTokenValid () {
    return (
      this.accessToken &&
      this.accessTokenExpiry &&
      Date.now() < this.accessTokenExpiry.getTime()
    )
  }

  /**
   * Retrieves a valid access token, potentially renewing it.
   */
  private getAccessToken () {
    return new Promise((resolve, reject) => {
      if (this.isAccessTokenValid()) {
        resolve(this.accessToken)
      } else {
        this.renewTokens().then((authResult: any) => {
          resolve(authResult.accessToken)
        }, reject)
      }
    })
  }
}

export default new AuthenticationService()
